import React, { memo, useMemo } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { PayloadAction } from '@reduxjs/toolkit';
import { ColumnDef } from '@tanstack/react-table';

import { Icons } from '@components/common';
import PageTitle from '@components/common/PageTitle';
import Table from '@components/common/Table/Table';

import { IPageSize } from '@interfaces/common/store';
import {
	ITableDatepickerFilter,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import styles from './AssetRentRequests.module.scss';

interface IAssetRentRequestsParams {
	pageTitle: string;
	pageTitleIcon?: React.ReactElement;
	getColumns: (navigate: NavigateFunction) => ColumnDef<any>[];
	filters?: TTableFilter[];
	dateFilters?: Array<ITableDatepickerFilter>;
	setSearchValue: (value: string) => void;
	searchValue?: string;
	isLoading: boolean;
	page: number;
	pageSize: number;
	sort: ITableSort;
	countOfRecords: number;
	requests: any;
	handleSetPage: (page: number) => void;
	handleSetPageSize: (page: number) => PayloadAction<IPageSize>;
}

function AssetRentRequests({
	pageTitle,
	pageTitleIcon: PageTitleIcon,
	getColumns,
	filters = [],
	dateFilters = [],
	setSearchValue,
	page,
	pageSize,
	requests,
	countOfRecords,
	isLoading,
	sort,
	handleSetPage,
	handleSetPageSize,
	searchValue,
}: IAssetRentRequestsParams) {
	const navigate = useNavigate();

	const columns = useMemo(() => getColumns(navigate), [getColumns, navigate]);

	const search: Array<ITableSearch> = useMemo(() => {
		return [
			{
				initialValue: searchValue || '',
				handler: setSearchValue,
				tooltipText: 'Search by request ID',
				name: 'searchValue',
			},
		];
	}, [searchValue, setSearchValue]);

	return (
		<div className={styles.requestsContainer}>
			<PageTitle
				icon={PageTitleIcon || Icons.assetsRequestsLight()}
				title={pageTitle}
			/>
			<div className={styles.requestTableContainer}>
				<Table
					columns={columns}
					data={requests || []}
					dateFilters={dateFilters}
					filters={filters}
					isEditAccess={false}
					isLoading={isLoading}
					isOpenSearch={!!searchValue}
					isWithoutCheckbox
					pagination={{
						countOfRecords,
						size: pageSize,
						forcePage: page,
						pageHandler: handleSetPage,
						pageSizeHandler: handleSetPageSize,
					}}
					search={search}
					searchThreshold={0}
					sort={sort}
				/>
			</div>
		</div>
	);
}

export default memo(AssetRentRequests);
